import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Plus, Minus, ShieldCheck, ShieldOff, AlertCircle, Check } from "lucide-react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const statusStyle = {
  Pending: "text-amber-400 bg-amber-400/10",
  Confirming: "text-amber-400 bg-amber-400/10",
  Completed: "text-emerald-400 bg-emerald-400/10",
  Rejected: "text-rose-400 bg-rose-400/10",
  active: "text-emerald-400 bg-emerald-400/10",
  paused: "text-amber-400 bg-amber-400/10",
  stopped: "text-white/45 bg-white/5",
};

const AdminUserDetail = () => {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [delta, setDelta] = useState(0);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/users/${userId}`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load.");
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const applyAdjust = async () => {
    if (!delta) { setErr("Enter a non-zero amount"); return; }
    if (reason.trim().length < 2) { setErr("Reason required"); return; }
    setBusy(true); setErr(null); setMsg(null);
    try {
      const { data: res } = await api.post(`/admin/users/${userId}/adjust-balance`, { delta: Number(delta), reason: reason.trim() });
      setMsg(`Balance now $${fmt(res.new_balance)}`);
      setDelta(0); setReason("");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const releaseLock = async () => {
    if (!data?.user?.locked_until) return;
    if (!window.confirm("Release the 30-day copy-trading withdrawal lock on this user?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/release-lock`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleSuspend = async () => {
    if (!data?.user) return;
    const next = !data.user.suspended;
    if (!window.confirm(next ? "Suspend this user? They won't be able to log in." : "Re-activate this user?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/toggle-suspend`, { suspended: next });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <AdminLayout title="User" subtitle="Loading">
        <div className="py-16 text-center text-white/40 text-[13px]">Loading…</div>
      </AdminLayout>
    );
  }

  const u = data.user;
  const t = data.totals || {};
  const copiesValue = (data.copies || []).filter((c) => c.status !== "stopped").reduce((s, c) => s + Number(c.current_value || 0), 0);

  return (
    <AdminLayout title={u.name} subtitle={u.email}>
      <Link to="/admin/users" className="inline-flex items-center gap-1.5 text-[12px] text-white/55 hover:text-white mb-4">
        <ChevronLeft size={14} /> Back to users
      </Link>

      <div className="grid lg:grid-cols-3 gap-4 lg:gap-5 mb-5">
        <div className="lg:col-span-2 bb-card rounded-2xl p-5 lg:p-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#3b59ff] to-[#1c3df5] flex items-center justify-center text-[16px] font-bold">
              {(u.name || "").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-[22px] text-white">{u.name}</div>
              <div className="text-[13px] text-white/55">{u.email}</div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                {u.phone && (
                  <span className="text-[12px] text-white/65 tabular-nums" data-testid="user-detail-phone">
                    {u.phone}
                  </span>
                )}
                {u.referral_source && (
                  <span
                    data-testid="user-detail-source"
                    className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-white/[0.05] text-white/70"
                  >
                    via {u.referral_source}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-white/35 mt-1 font-mono">{u.id}</div>
            </div>
            <button
              onClick={toggleSuspend}
              disabled={busy}
              className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border text-[12px] font-medium ${
                u.suspended
                  ? "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                  : "border-rose-500/30 text-rose-400 hover:bg-rose-500/10"
              }`}
            >
              {u.suspended ? <><ShieldCheck size={13} /> Reactivate</> : <><ShieldOff size={13} /> Suspend</>}
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-5 border-t border-white/5">
            <div>
              <div className="text-[11px] text-white/45">Balance</div>
              <div className="font-display font-bold text-[20px] text-white tabular-nums">${fmt(u.balance)}</div>
            </div>
            <div>
              <div className="text-[11px] text-white/45">In copy trades</div>
              <div className="font-display font-bold text-[20px] text-white tabular-nums">${fmt(copiesValue)}</div>
            </div>
            <div>
              <div className="text-[11px] text-white/45">KYC</div>
              <div className="text-[14px] text-white capitalize">{u.kyc_status}</div>
            </div>
            <div>
              <div className="text-[11px] text-white/45">Role</div>
              <div className="text-[14px] text-white capitalize">{(u.role || "user").replace("_", " ")}</div>
            </div>
          </div>

          {u.locked_until && (() => {
            const lockEnd = new Date(u.locked_until);
            const now = new Date();
            const active = lockEnd > now;
            if (!active) return null;
            const daysLeft = Math.ceil((lockEnd - now) / (1000 * 60 * 60 * 24));
            return (
              <div
                data-testid="user-detail-lock"
                className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 rounded-xl bg-amber-400/10 border border-amber-400/30"
              >
                <div className="flex items-start gap-2.5">
                  <div className="text-amber-300 text-[16px] leading-none mt-0.5">🔒</div>
                  <div>
                    <div className="text-[13px] text-amber-100 font-semibold">
                      Withdrawal lock active — {daysLeft} day{daysLeft === 1 ? "" : "s"} remaining
                    </div>
                    <div className="text-[11px] text-amber-100/70 mt-0.5">
                      Funds locked until {lockEnd.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} (30-day copy-trading lock-in)
                    </div>
                  </div>
                </div>
                <button
                  onClick={releaseLock}
                  disabled={busy}
                  data-testid="user-detail-release-lock"
                  className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-amber-400/40 text-amber-200 hover:bg-amber-400/15 text-[12px] font-medium whitespace-nowrap"
                >
                  Release lock
                </button>
              </div>
            );
          })()}
        </div>

        <div className="bb-card rounded-2xl p-5 lg:p-6">
          <div className="font-display font-semibold text-[15px] mb-1">Adjust balance</div>
          <p className="text-[11px] text-white/45 mb-4">Positive credits, negative debits. Logged in audit trail.</p>
          <div className="flex items-center gap-1 mb-3">
            <button onClick={() => setDelta((d) => Number((d - 10).toFixed(2)))} className="w-9 h-9 rounded-lg border border-white/10 text-white/60 hover:text-white"><Minus size={14} className="mx-auto" /></button>
            <input
              type="number"
              value={delta}
              onChange={(e) => setDelta(Number(e.target.value))}
              className="flex-1 h-9 bg-white/[0.02] border border-white/8 rounded-lg px-3 text-center text-[14px] text-white tabular-nums outline-none focus:border-[#ff7a1a]"
            />
            <button onClick={() => setDelta((d) => Number((d + 10).toFixed(2)))} className="w-9 h-9 rounded-lg border border-white/10 text-white/60 hover:text-white"><Plus size={14} className="mx-auto" /></button>
          </div>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (required)"
            className="w-full h-9 px-3 mb-3 bg-white/[0.02] border border-white/8 rounded-lg text-[12px] text-white outline-none focus:border-[#ff7a1a] placeholder:text-white/30"
          />
          <button
            onClick={applyAdjust}
            disabled={busy}
            className="w-full h-10 rounded-lg text-white text-[13px] font-medium"
            style={{ background: "linear-gradient(135deg,#ff7a1a,#c2410c)" }}
          >
            Apply
          </button>
          {err && <div className="mt-2 flex items-start gap-1.5 text-[11px] text-rose-300"><AlertCircle size={12} className="shrink-0 mt-0.5" /> {err}</div>}
          {msg && <div className="mt-2 flex items-start gap-1.5 text-[11px] text-emerald-400"><Check size={12} className="shrink-0 mt-0.5" /> {msg}</div>}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5" data-testid="user-money-summary">
        <div className="bb-card rounded-2xl p-5" data-testid="summary-balance">
          <div className="text-[11px] text-white/45 mb-1">Current balance</div>
          <div className="font-display font-bold text-[22px] text-white tabular-nums">${fmt(t.balance ?? u.balance)}</div>
        </div>
        <div className="bb-card rounded-2xl p-5" data-testid="summary-deposited">
          <div className="text-[11px] text-white/45 mb-1">Total deposited</div>
          <div className="font-display font-bold text-[22px] text-emerald-400 tabular-nums">${fmt(t.deposited)}</div>
        </div>
        <div className="bb-card rounded-2xl p-5" data-testid="summary-withdrawn">
          <div className="text-[11px] text-white/45 mb-1">Total withdrawn</div>
          <div className="font-display font-bold text-[22px] text-rose-400 tabular-nums">${fmt(t.withdrawn)}</div>
        </div>
        <div className="bb-card rounded-2xl p-5" data-testid="summary-commission">
          <div className="text-[11px] text-white/45 mb-1">Affiliate commission</div>
          <div className="font-display font-bold text-[22px] text-violet-400 tabular-nums">${fmt(t.affiliate_commission)}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 lg:gap-5">
        <div className="bb-card rounded-2xl p-5">
          <div className="font-display font-semibold text-[14px] mb-3">Deposits ({data.deposits.length})</div>
          <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
            {data.deposits.length === 0 && <div className="text-[12px] text-white/40">None</div>}
            {data.deposits.map((d) => (
              <div key={d.id} className="flex items-center gap-2 text-[12px] py-1.5 border-b border-white/5 last:border-0">
                <span className="text-emerald-400 tabular-nums w-16">+${fmt(d.amount)}</span>
                <span className="text-white/55 truncate flex-1">{d.method_label || d.method}</span>
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${statusStyle[d.status] || "text-white/55 bg-white/[0.05]"}`}>{d.status}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bb-card rounded-2xl p-5">
          <div className="font-display font-semibold text-[14px] mb-3">Withdrawals ({data.withdrawals.length})</div>
          <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
            {data.withdrawals.length === 0 && <div className="text-[12px] text-white/40">None</div>}
            {data.withdrawals.map((w) => (
              <div key={w.id} className="flex items-center gap-2 text-[12px] py-1.5 border-b border-white/5 last:border-0">
                <span className="text-rose-400 tabular-nums w-16">-${fmt(w.amount)}</span>
                <span className="text-white/55 truncate flex-1 capitalize">{w.method}</span>
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${statusStyle[w.status] || "text-white/55 bg-white/[0.05]"}`}>{w.status}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bb-card rounded-2xl p-5">
          <div className="font-display font-semibold text-[14px] mb-3">Copy allocations ({data.copies.length})</div>
          <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
            {data.copies.length === 0 && <div className="text-[12px] text-white/40">None</div>}
            {data.copies.map((c) => (
              <div key={c.id} className="flex items-center gap-2 text-[12px] py-1.5 border-b border-white/5 last:border-0">
                <span className="text-white tabular-nums w-16">${fmt(c.current_value)}</span>
                <span className="text-white/55 truncate flex-1">{c.trader_name}</span>
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${statusStyle[c.status] || "text-white/55 bg-white/[0.05]"}`}>{c.status}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bb-card rounded-2xl p-5" data-testid="user-commissions-card">
          <div className="font-display font-semibold text-[14px] mb-3">Affiliate commissions ({(data.commissions || []).length})</div>
          <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
            {(data.commissions || []).length === 0 && <div className="text-[12px] text-white/40">None</div>}
            {(data.commissions || []).map((c) => (
              <div key={c.id} className="flex items-center gap-2 text-[12px] py-1.5 border-b border-white/5 last:border-0">
                <span className="text-violet-400 tabular-nums w-16">+${fmt(c.amount)}</span>
                <span className="text-white/55 truncate flex-1">from {c.from_user_name}</span>
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded text-white/55 bg-white/[0.05]">L{c.level} {Math.round((c.pct || 0) * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUserDetail;
